// EventCalendar.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import nbLocale from "@fullcalendar/core/locales/nb";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function EventCalendar() {
  const [events, setEvents] = useState([]);
  const [initialDate, setInitialDate] = useState(new Date());
  const calendarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Første innlasting – bruk ?date= om den finnes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateParam = params.get("date");

    if (dateParam) {
      const d = new Date(dateParam);
      if (!isNaN(d)) {
        setInitialDate(d);
      }
    }
  }, [location.search]);


  // Hvis ?date= endres etter mount → hopp til den datoen
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateParam = params.get("date");

    if (!dateParam) return;
    if (!calendarRef.current) return;

    const d = new Date(dateParam);
    if (isNaN(d)) return;

    const api = calendarRef.current.getApi();
    api.gotoDate(d);
  }, [location.search]);

  // Hent events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/events`);
        const data = await res.json();

        const formatted = data.map((ev) => {
          let start = ev.start;
          let end = ev.end;

          // Heldagsevent: gjør sluttdato inklusiv
          if (ev.allDay && end) {
            const endDate = new Date(end);
            endDate.setDate(endDate.getDate() + 1);
            end = endDate.toISOString();
          }

          return {
            id: ev._id,
            title: ev.title,
            start,
            end,
            allDay: ev.allDay,
            color: getEventColor(ev),
            extendedProps: ev,
          };
        });

        setEvents(formatted);
      } catch (e) {
        console.error("Feil ved henting av events:", e);
      }
    };

    fetchEvents();
  }, []);

  // Farger per kategori
  const getEventColor = (ev) => {
    switch (ev.category) {
      case "FRIDAG":
        return "#DC2626";
      case "MERKEDAG":
        return "#8a26dcff";
      case "STAND":
        return "#EC4899";
      case "KAMPANJE":
        return "#F59E0B";
      case "INTERN":
        return "#3B82F6";
      case "AKTIVITET":
      default:
        return "#10B981";
    }
  };

  // Brukes av "Registrer event"-knappen: første dag i måneden som vises
  const getCurrentMonthAnchorIso = () => {
    const api = calendarRef.current?.getApi();
    const d = api ? api.getDate() : new Date();
    const firstOfMonth = new Date(d.getFullYear(), d.getMonth(), 1);
    return firstOfMonth.toISOString().slice(0, 10);
  };

  return (
    <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-lg p-4">
      {/* Toppseksjon */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-2xl font-bold">Årshjul – Kalender</h2>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/eventstats")}
            className="bg-blue-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-600"
          >
            Statistikk
          </button>

          <button
            type="button"
            onClick={() => {
              const monthParam = getCurrentMonthAnchorIso();
              navigate(`/createevent?from=${monthParam}`);
            }}
            className="bg-green-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-600"
          >
            Registrer event
          </button>
        </div>
      </div>


      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        initialDate={initialDate}
        events={events}
        height="auto"
        locale={nbLocale}
        firstDay={1}
        weekNumbers={true}
        weekNumberCalculation="ISO"
        headerToolbar={{
          left: "prev,next",
          center: "title",
          right: "today",
        }}
        buttonText={{
          today: "I dag",
          month: "Måned",
          week: "Uke",
          day: "Dag",
        }}
        views={{
          dayGridMonth: { titleFormat: { month: "long", year: "numeric" } },
        }}
        handleWindowResize={true}
        windowResize={(view) => {
          const width = window.innerWidth;
          if (width < 640) {
            view.calendar.changeView("dayGridMonth");
          }
        }}

        // 👉 Klikk på event → bruk eventets startdato som ?from=
        eventClick={(info) => {
          info.jsEvent.preventDefault();
          info.jsEvent.stopPropagation();

          const start = info.event.start || new Date();
          const fromParam = start.toISOString().slice(0, 10); // faktiske startdatoen

          navigate(`/events/${info.event.id}?from=${fromParam}`);
        }}

        // 👉 Klikk på tom rute → bruk klikket dato som både date og from
        dateClick={(info) => {
          const day = info.dateStr; // 'YYYY-MM-DD'
          navigate(`/createevent?date=${day}&from=${day}`);
        }}
      />
    </div>
  );
}
